import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { utils, writeFile } from 'xlsx';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import NotificationPanel from '../components/NotificationPanel';
import { useEmployees } from '../hooks/useEmployees';
import type { EmployeeListItem } from '../api/employees';
import { getEmployeeState, isContractExpired } from '../utils/employeeStatus';

// Composant de Carte d'Employé
const EmployeeCard: React.FC<{ employee: EmployeeListItem }> = ({ employee }) => {
    const navigate = useNavigate();

    const handleClick = () => {
        navigate(`/employees/${employee.id}`); 
    };

    // SVG de profil par défaut
    const ProfileSvg = (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-16 h-16 text-warning/50">
            <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.03 18.03 0 0 1 12 21.75c-2.73 0-5.41-.608-7.812-1.79a.75.75 0 0 1-.437-.695Z" clipRule="evenodd" />
        </svg>
    );

    return (
        <div 
            className="card bg-base-100 shadow-xl cursor-pointer hover:shadow-2xl hover:bg-base-200 transition-all duration-200"
            onClick={handleClick}
        >
            <div className="card-body p-5">
                <div className="flex items-center gap-4 mb-2">
                    <div className="avatar placeholder">
                        <div className="bg-neutral-focus text-neutral-content rounded-full w-16">
                            {employee.photo ? (
                                <img src={employee.photo} alt={employee.full_name} className="rounded-full" />
                            ) : (
                                ProfileSvg
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col gap-1">
                        <h2 className="text-xl font-bold text-warning leading-tight">
                            {employee.full_name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Nom non défini'}
                        </h2>
                        <p className="text-xs text-base-content/60 font-mono">
                            Matricule: {employee.employee_id || 'Non attribué'}
                        </p>
                    </div>
                </div>
                
                <p className="text-md font-medium mt-1 border-t border-base-300 pt-4">
                    Poste: {employee.position_display}
                </p>
                <p className="text-sm text-base-content/70">
                    Département: {employee.department_name}
                </p>
                <p className="text-sm text-base-content/70">
                    Société: {employee.company_name}
                </p>
                <div className="card-actions justify-end mt-2">
                    {/* Badge d'état : même règle que la fiche employé */}
                    {(() => {
                        const etat = getEmployeeState(employee);
                        return <span className={`badge ${etat.badgeClass}`}>{etat.label}</span>;
                    })()}
                    <button className="btn btn-sm btn-outline btn-warning">Voir Profil</button>
                </div>
            </div>
        </div>
    );
};

const EXPORT_COLUMNS = [
    { key: 'employee_id', label: 'Matricule' },
    { key: 'full_name', label: 'Nom Complet' },
    { key: 'first_name', label: 'Prénom' },
    { key: 'last_name', label: 'Nom' },
    { key: 'date_of_birth', label: 'Date de Naissance' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Téléphone' },
    { key: 'department_name', label: 'Département' },
    { key: 'company_name', label: 'Société' },
    { key: 'position_display', label: 'Poste' },
    { key: 'professional_category', label: 'Catégorie Pro' },
    { key: 'contract_type', label: 'Type Contrat' },
    { key: 'contract_start_date', label: 'Début Contrat' },
    { key: 'contract_end_date', label: 'Fin Contrat' },
    { key: 'contract_status', label: 'Statut Contrat' },
    { key: 'salaire_categoriel', label: 'Salaire Catégoriel' },
    { key: 'total_salary', label: 'Salaire Total' },
    { key: 'status', label: 'Statut' },
    { key: 'seniority', label: 'Ancienneté' },
    { key: 'created_at', label: 'Date Création' },
];

const Employees: React.FC = () => {
    // Hook pour charger les données du backend
    const { employees, loading, error, refetch } = useEmployees();
    
    // État pour la notification
    const [isNotifPanelOpen, setIsNotifPanelOpen] = useState(false);
    
    // État pour la recherche
    const [searchQuery, setSearchQuery] = useState('');
    
    // État pour le filtre de statut
    const [statusFilter, setStatusFilter] = useState<'TOUS' | 'ACTIF' | 'INACTIF'>('TOUS');

    // Filtres pilotés par l'URL (depuis les cartes du tableau de bord)
    //   /employees?contrat=CDI        → uniquement les CDI
    //   /employees?recents=1          → embauchés ce mois-ci
    //   /employees?departement=Compta → un département
    const [searchParams, setSearchParams] = useSearchParams();
    const contractFilter = (searchParams.get('contrat') || '').toUpperCase();
    const recentsOnly = searchParams.get('recents') === '1';
    const departmentFilter = searchParams.get('departement') || '';
    const companyFilter = searchParams.get('societe') || '';
    const contractStateFilter = (searchParams.get('statut') || '').toUpperCase(); // ACTIF | INACTIF
    const hasUrlFilter = Boolean(
        contractFilter || recentsOnly || departmentFilter || companyFilter || contractStateFilter
    );

    const urlFilterLabel = contractFilter
        ? `Contrats ${contractFilter}`
        : recentsOnly
        ? 'Nouveaux employés du mois'
        : departmentFilter
        ? `Département : ${departmentFilter}`
        : companyFilter
        ? `Société : ${companyFilter}`
        : contractStateFilter === 'ACTIF'
        ? 'Contrat en cours'
        : contractStateFilter === 'INACTIF'
        ? 'Contrat terminé'
        : '';

    const clearUrlFilter = () => setSearchParams({});

    // Même définition que le backend (dashboard_stats) : contrat démarré
    // sur l'année ET le mois en cours. Comparaison sur la chaîne "YYYY-MM"
    // pour éviter tout décalage de fuseau horaire.
    const isNewThisMonth = (employee: EmployeeListItem): boolean => {
        if (!employee.contract_start_date) return false;
        const now = new Date();
        const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        return employee.contract_start_date.startsWith(yearMonth);
    };

    // Fonction d'ouverture/fermeture passée à la Navbar
    const handleOpenPanel = () => setIsNotifPanelOpen(true);
    const handleClosePanel = () => setIsNotifPanelOpen(false);

    // Debug: afficher les employés chargés
    React.useEffect(() => {
        console.log('📊 Employés chargés:', employees.length);
        console.log('👥 Liste:', employees.map(e => `${e.full_name} (${e.employee_id})`).join(', '));
        
        // Debug: Analyser les contrats expirés
        const employeesWithContractEnd = employees.filter(e => e.contract_end_date);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        
        console.log('🔍 Debug contrats:');
        employeesWithContractEnd.forEach(emp => {
            const endDate = new Date(emp.contract_end_date!);
            endDate.setHours(0, 0, 0, 0);
            const isActive = endDate >= now;
            console.log(`- ${emp.full_name}: ${emp.contract_end_date} -> ${isActive ? 'ACTIF' : 'EXPIRÉ'}`);
        });
        
        const activeCount = employees.filter(emp => isEmployeeActive(emp)).length;
        const inactiveCount = employees.length - activeCount;
        console.log(`📈 Résumé: ${activeCount} actifs, ${inactiveCount} inactifs sur ${employees.length} total`);
    }, [employees]);

    // Fonction pour normaliser le texte (enlever accents, espaces multiples et mettre en minuscules)
    const normalizeText = (text: string): string => {
        return text
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
            .replace(/\s+/g, ' ') // Remplace espaces multiples par un seul
            .trim();
    };

    // Filtres « contrat en cours / terminé » : basés sur la seule échéance du
    // contrat, comme les cartes du tableau de bord. Définition unique dans
    // utils/employeeStatus.
    const isEmployeeActive = (employee: EmployeeListItem): boolean =>
        !isContractExpired(employee);

    // --- Logique de Recherche et Filtrage ---
    const filteredEmployees = employees.filter(employee => {
        // Filtre de recherche textuelle
        let matchesSearch = true;
        if (searchQuery.trim()) {
            const query = normalizeText(searchQuery);
            const fullName = normalizeText(employee.full_name || '');
            const firstName = normalizeText(employee.first_name || '');
            const lastName = normalizeText(employee.last_name || '');
            const employeeId = normalizeText(employee.employee_id || '');
            const department = normalizeText(employee.department_name || '');
            const company = normalizeText(employee.company_name || '');
            const position = normalizeText(employee.position_display || '');
            
            matchesSearch = fullName.includes(query) ||
                firstName.includes(query) ||
                lastName.includes(query) ||
                employeeId.includes(query) ||
                department.includes(query) ||
                company.includes(query) ||
                position.includes(query);
        }
        
        // Filtre de statut contractuel
        let matchesStatus = true;
        if (statusFilter !== 'TOUS') {
            const isActive = isEmployeeActive(employee);
            matchesStatus = statusFilter === 'ACTIF' ? isActive : !isActive;
        }

        // Filtres venant de l'URL (cartes du tableau de bord)
        const matchesContract = !contractFilter || (employee.contract_type || '').toUpperCase() === contractFilter;
        const matchesRecent = !recentsOnly || isNewThisMonth(employee);
        const matchesDepartment = !departmentFilter || (employee.department_name || '') === departmentFilter;
        const matchesCompany = !companyFilter || (employee.company_name || '') === companyFilter;
        // "Contrat en cours" / "Contrat terminé" : même règle que la carte du
        // tableau de bord, basée sur la date de fin de contrat.
        const matchesContractState =
            !contractStateFilter ||
            (contractStateFilter === 'ACTIF' ? isEmployeeActive(employee) : !isEmployeeActive(employee));

        return (
            matchesSearch && matchesStatus && matchesContract && matchesRecent &&
            matchesDepartment && matchesCompany && matchesContractState
        );
    });

    // --- Logique de Pagination ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 16; // 16 cartes par page
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentEmployees = filteredEmployees.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
    const paginate = (pageNumber: number) => {
        if (pageNumber > 0 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };
    
    // Réinitialiser la page lors d'une nouvelle recherche ou changement de filtre
    React.useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, statusFilter, contractFilter, recentsOnly, departmentFilter, companyFilter, contractStateFilter]);

    // --- Export Excel ---
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [selectedExportColumns, setSelectedExportColumns] = useState<string[]>(EXPORT_COLUMNS.map(c => c.key));

    const handleOpenExportModal = () => {
        setIsExportModalOpen(true);
    };

    const toggleColumn = (key: string) => {
        setSelectedExportColumns(prev => 
            prev.includes(key) 
                ? prev.filter(k => k !== key)
                : [...prev, key]
        );
    };

    const selectAllColumns = () => setSelectedExportColumns(EXPORT_COLUMNS.map(c => c.key));
    const deselectAllColumns = () => setSelectedExportColumns([]);

    const executeExport = () => {
        const dataToExport = filteredEmployees.map(emp => {
            const row: any = {};
            EXPORT_COLUMNS.forEach(col => {
                if (selectedExportColumns.includes(col.key)) {
                    // @ts-ignore
                    row[col.label] = emp[col.key] || '';
                }
            });
            return row;
        });

        const ws = utils.json_to_sheet(dataToExport);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, "Employés");
        writeFile(wb, `Export_Employés_${new Date().toISOString().split('T')[0]}.xlsx`);
        setIsExportModalOpen(false);
    };
    // ----------------------------

    if (loading) {
        return (
            <div className="flex h-screen bg-base-200">
                <Sidebar />
                <div className="flex-1 flex items-center justify-center">
                    <span className="loading loading-spinner loading-lg text-warning"></span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-screen bg-base-200">
                <Sidebar />
                <div className="flex-1 flex flex-col items-center justify-center gap-4">
                    <div className="alert alert-error max-w-md">
                        <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                            <div className="font-bold">Erreur de chargement</div>
                            <div className="text-sm">{error}</div>
                        </div>
                    </div>
                    <button 
                        onClick={refetch}
                        className="btn btn-warning"
                    >
                        🔄 Réessayer
                    </button>
                </div>
            </div>
        );
    }

    // État vide - pas d'employés
    if (employees.length === 0) {
        return (
            <div className={`flex h-screen bg-base-200 ${isNotifPanelOpen ? 'overflow-hidden' : ''}`}>
                <Sidebar />
                
                <div className="flex-1 p-6 overflow-y-auto">
                    <Navbar onOpenNotifPanel={handleOpenPanel} />
                    
                    <div className="flex flex-col items-center justify-center min-h-[70vh]">
                        <div className="text-center max-w-md">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-24 w-24 mx-auto text-base-content/30 mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <h2 className="text-2xl font-bold text-base-content mb-3">Aucun employé trouvé</h2>
                            <p className="text-base-content/70 mb-6">
                                La liste des employés est vide. Créez votre premier employé pour commencer.
                            </p>
                            <button 
                                onClick={() => window.location.href = '/creation-employes'}
                                className="btn btn-primary btn-lg gap-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                </svg>
                                Créer un employé
                            </button>
                        </div>
                    </div>
                </div>
                
                <NotificationPanel isOpen={isNotifPanelOpen} onClose={handleClosePanel} />
            </div>
        );
    }

    return (
        // L'overflow-hidden empêche le défilement du fond lorsque le panneau est ouvert
        <div className={`flex h-screen bg-base-200 ${isNotifPanelOpen ? 'overflow-hidden' : ''}`}> 
            <Sidebar />
            
            <div className="flex-1 p-6 overflow-y-auto">
                {/* 1. Passer la fonction d'ouverture à la Navbar */}
                <Navbar onOpenNotifPanel={handleOpenPanel} />
                
                <div className="flex flex-col gap-6">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4 w-full sm:w-auto">
                            <h2 className="text-3xl font-extrabold text-base-content">Liste des Employés</h2>
                            <button 
                                onClick={handleOpenExportModal}
                                className="btn btn-sm btn-success gap-2 text-white ml-auto sm:ml-4 px-6"
                                title="Exporter en Excel"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                </svg>
                                Exporter
                            </button>
                        </div>
                        <div className="text-sm text-base-content/70">
                            {filteredEmployees.length} employé{filteredEmployees.length > 1 ? 's' : ''}
                            {statusFilter !== 'TOUS' && (
                                <span className="ml-2 text-xs opacity-75">
                                    ({statusFilter === 'ACTIF' ? 'actifs' : 'inactifs'})
                                </span>
                            )}
                            {searchQuery && (
                                <span className="ml-2 text-xs opacity-75">
                                    (recherche: "{searchQuery}")
                                </span>
                            )}
                        </div>
                    </div>
                    
                    {/* Bandeau du filtre venant du tableau de bord */}
                    {hasUrlFilter && (
                        <div className="alert bg-warning/15 border border-warning/40 py-3">
                            <span className="text-sm">
                                Filtre actif : <span className="font-bold">{urlFilterLabel}</span>
                                <span className="opacity-70"> — {filteredEmployees.length} employé{filteredEmployees.length > 1 ? 's' : ''}</span>
                            </span>
                            <button className="btn btn-sm btn-ghost" onClick={clearUrlFilter}>
                                ✕ Retirer le filtre
                            </button>
                        </div>
                    )}

                    {/* Barre de recherche et filtres */}
                    <div className="flex flex-col sm:flex-row gap-4 w-full">
                        {/* Barre de recherche */}
                        <div className="form-control w-full max-w-md">
                            <div className="input-group">
                                <input 
                                    type="text" 
                                    placeholder="🔍 Rechercher par nom, matricule, département, société..." 
                                    className="input input-bordered w-full"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                {searchQuery && (
                                    <button 
                                        className="btn btn-square btn-ghost"
                                        onClick={() => setSearchQuery('')}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        {/* Filtre par statut contractuel */}
                        <div className="form-control w-full max-w-xs">
                            <div className="input-group">
                                <span className="label-text sr-only">Filtrer par statut</span>
                                <select 
                                    className="select select-bordered w-full"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value as 'TOUS' | 'ACTIF' | 'INACTIF')}
                                >
                                    <option value="TOUS">📋 Liste des employés</option>
                                    <option value="ACTIF">✅ Employés actifs</option>
                                    <option value="INACTIF">❌ Employés inactifs</option>
                                </select>
                                {statusFilter !== 'TOUS' && (
                                    <button 
                                        className="btn btn-square btn-ghost"
                                        onClick={() => setStatusFilter('TOUS')}
                                        title="Réinitialiser le filtre"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Aucun résultat pour les filtres en cours */}
                    {filteredEmployees.length === 0 && (
                        <div className="text-center py-16 bg-base-100 rounded-lg">
                            <p className="text-lg font-semibold text-base-content/70">Aucun employé ne correspond</p>
                            <p className="text-sm text-base-content/50 mt-1">
                                {urlFilterLabel || 'Modifiez la recherche ou les filtres.'}
                            </p>
                            {hasUrlFilter && (
                                <button className="btn btn-sm btn-warning mt-4" onClick={clearUrlFilter}>
                                    Voir tous les employés
                                </button>
                            )}
                        </div>
                    )}

                    {/* Grille des cartes */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {currentEmployees.map(employee => (
                            <EmployeeCard key={employee.id} employee={employee} />
                        ))}
                    </div>

                    {/* Contrôles de Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-center mt-6">
                            <div className="join">
                                <button 
                                    className="join-item btn btn-warning" 
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    «
                                </button>
                                <button className="join-item btn btn-warning">
                                    Page {currentPage} / {totalPages}
                                </button>
                                <button 
                                    className="join-item btn btn-warning" 
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    »
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* 2. Inclure le Panneau de Notifications */}
            <NotificationPanel 
                isOpen={isNotifPanelOpen} 
                onClose={handleClosePanel} 
            />

            {/* Modal d'Export Excel */}
            {isExportModalOpen && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-3xl">
                        <h3 className="font-bold text-lg mb-4">Exporter les données employés</h3>
                        <p className="text-sm text-base-content/70 mb-4">
                            Sélectionnez les colonnes à inclure dans le fichier Excel.
                        </p>
                        
                        <div className="flex gap-2 mb-4">
                            <button onClick={selectAllColumns} className="btn btn-xs btn-outline">Tout sélectionner</button>
                            <button onClick={deselectAllColumns} className="btn btn-xs btn-outline">Tout désélectionner</button>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto p-2 border rounded-lg">
                            {EXPORT_COLUMNS.map(col => (
                                <label key={col.key} className="label cursor-pointer justify-start gap-3 hover:bg-base-200 rounded p-2">
                                    <input 
                                        type="checkbox" 
                                        className="checkbox checkbox-sm checkbox-primary"
                                        checked={selectedExportColumns.includes(col.key)}
                                        onChange={() => toggleColumn(col.key)}
                                    />
                                    <span className="label-text">{col.label}</span>
                                </label>
                            ))}
                        </div>

                        <div className="modal-action">
                            <button className="btn" onClick={() => setIsExportModalOpen(false)}>Annuler</button>
                            <button 
                                className="btn btn-success text-white" 
                                onClick={executeExport}
                                disabled={selectedExportColumns.length === 0}
                            >
                                Télécharger Excel ({selectedExportColumns.length} colonnes)
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Employees;